"use client";

import React, { useEffect } from "react";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { sortSizes } from "@/lib/sizes";

export interface SizeChartData {
  columns: string[];
  rows: string[][];
  note?: string;
}

export function parseSizeChart(raw: string): SizeChartData | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed.columns && Array.isArray(parsed.rows) && parsed.rows.length > 0) {
      // Rijen klein → groot, op de eerste kolom (de maat). Bron-volgorde is niet
      // altijd al gesorteerd (import/kopieerwerk).
      return { ...parsed, rows: sortSizes(parsed.rows, (row: string[]) => row[0]) };
    }
  } catch {}
  // Backwards compat: "XS=60-70, S=70-80" formaat
  const rows = raw.split(/[,\n]/).map((s) => s.trim()).filter(Boolean)
    .map((s) => { const [size, ...rest] = s.split("="); return [size.trim(), rest.join("=").trim()]; })
    .filter((r) => r[0]);
  if (rows.length === 0) return null;
  return { columns: ["Maat", "Omtrek (cm)"], rows };
}

interface Props {
  open: boolean;
  onClose: () => void;
  data: SizeChartData;
  productName?: string;
}

export default function SizeChartDrawer({ open, onClose, data, productName }: Props) {
  const s = useSiteSettings();
  // Sluit met Escape-toets
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Voorkom body-scroll wanneer drawer open is
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1100,
          opacity: open ? 1 : 0, pointerEvents: open ? "auto" : "none",
          transition: "opacity 0.28s ease",
        }}
      />

      {/* Drawer */}
      <div
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 1101,
          width: "min(520px, 100vw)",
          background: "#fff",
          boxShadow: "-8px 0 40px rgba(0,0,0,0.18)",
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.32s cubic-bezier(0.4,0,0.2,1)",
          display: "flex", flexDirection: "column",
          overflowY: "auto",
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Maattabel"
      >
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "20px 24px 16px",
          borderBottom: "1px solid var(--color-gray-200, #e8e8e8)",
          position: "sticky", top: 0, background: "#fff", zIndex: 1,
        }}>
          <div>
            <h2 style={{ fontSize: "1.1rem", fontWeight: 700, margin: 0 }}>Maattabel</h2>
            {productName && <p style={{ fontSize: "0.78rem", color: "#999", margin: "2px 0 0" }}>{productName}</p>}
          </div>
          <button
            onClick={onClose}
            aria-label="Sluiten"
            style={{
              border: "none", background: "none", cursor: "pointer",
              width: 36, height: 36, borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "1.3rem", color: "#555",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f3f3")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
          >
            ✕
          </button>
        </div>

        {/* Inhoud */}
        <div style={{ padding: "24px", flex: 1 }}>
          {/* Intro */}
          <p style={{ fontSize: "0.875rem", color: "#555", lineHeight: 1.65, marginBottom: "20px" }}>
            {s.sizeChartIntro}
          </p>

          {/* Maattabel */}
          <div style={{ overflowX: "auto", marginBottom: "24px", borderRadius: "8px", border: "1px solid #eee" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
              <thead>
                <tr style={{ background: "#f8f8f8" }}>
                  {data.columns.map((col, i) => (
                    <th key={i} style={{
                      padding: "11px 14px", textAlign: "left", fontWeight: 600,
                      borderBottom: "2px solid #e8e8e8",
                      borderRight: i < data.columns.length - 1 ? "1px solid #eee" : "none",
                      whiteSpace: "nowrap",
                      color: "#1a1a1a",
                    }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, ri) => (
                  <tr key={ri} style={{
                    borderBottom: "1px solid #f0f0f0",
                    background: ri % 2 === 0 ? "#fff" : "#fafafa",
                  }}>
                    {row.map((cell, ci) => (
                      <td key={ci} style={{
                        padding: "10px 14px",
                        borderRight: ci < row.length - 1 ? "1px solid #f0f0f0" : "none",
                        fontWeight: ci === 0 ? 600 : 400,
                        color: ci === 0 ? "#1a1a1a" : "#555",
                      }}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Optionele noot */}
          {data.note && (
            <p style={{ fontSize: "0.8rem", color: "#888", fontStyle: "italic", marginBottom: "24px" }}>
              {data.note}
            </p>
          )}

          {/* Meten is weten */}
          <div style={{ background: "#fdf8f8", border: "1px solid #f0e0e3", borderRadius: "10px", padding: "20px" }}>
            <h3 style={{ fontSize: "0.95rem", fontWeight: 700, marginBottom: "14px", color: "#720917" }}>
              {s.sizeChartMeasureTitle}
            </h3>
            <ol style={{ paddingLeft: "1.2rem", display: "flex", flexDirection: "column", gap: "10px" }}>
              {[s.sizeChartStep1, s.sizeChartStep2, s.sizeChartStep3].filter(Boolean).map((step, i) => (
                <li key={i} style={{ fontSize: "0.85rem", lineHeight: 1.6, color: "#444" }}>{step}</li>
              ))}
            </ol>
            {s.sizeChartTip && (
              <p style={{ fontSize: "0.8rem", color: "#888", marginTop: "12px" }}>{s.sizeChartTip}</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
